import { formatInTimeZone } from 'date-fns-tz';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { FormControl, FormField, FormItem, FormMessage, Textarea } from '@crossnokaye/ux-common';

import {
  CancelShutdownFormData,
  CancelShutdownFormSchema,
} from '@/components/shutdown-banner/schema';
import { ShutdownDialog } from '@/components/shutdown-banner/shutdown-dialog';
import { createFacilityDate } from '@/helpers/datetime/facility-date';
import { formatUTCISO } from '@/helpers/datetime/utc-date';
import { useShutdownApp } from '@/hooks/query-hooks/scheduling';
import { usePatchScheduledAppSettings } from '@/hooks/query-hooks/settings';
import { useCurrentFacility } from '@/hooks/use-current-facility';
import { timezoneAtom } from '@/state/time.state';

export const CancelShutdownAction: React.FC<{
  endTime: Date | undefined;
  startTime: Date | undefined;
}> = ({ endTime, startTime }) => {
  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { data: shutdownApp } = useShutdownApp({
    orgId,
    agentId,
  });

  const { mutate: cancelShutdown } = usePatchScheduledAppSettings({
    orgId,
    agentId,
    appId: shutdownApp?.[0]?.appId,
  });

  const timeZone = useAtomValue(timezoneAtom);
  const cancelUntil = useMemo(() => {
    let cancelUntil = createFacilityDate();
    if (endTime) {
      cancelUntil = createFacilityDate(endTime);
    } else if (startTime) {
      cancelUntil = createFacilityDate(startTime);
    }

    // Set time to midnight of next day
    cancelUntil.setHours(24, 0, 0, 0);
    return cancelUntil;
  }, [endTime, startTime]);

  const form = useForm<CancelShutdownFormData>({
    resolver: zodResolver(CancelShutdownFormSchema),
    mode: 'onChange',
  });

  const onSubmit: SubmitHandler<CancelShutdownFormData> = async (data) => {
    await cancelShutdown([
      {
        name: 'shutdown_postpone_until',
        value: JSON.stringify(formatUTCISO(cancelUntil)),
        changeDescription: data.cancelShutdownReason,
      },
    ]);
  };

  return (
    <ShutdownDialog
      id={'cancel'}
      form={form}
      onSubmit={onSubmit}
      title={'Are you sure you want to cancel the upcoming Shutdown?'}
      description={`Shutdown will be cancelled until ${formatInTimeZone(cancelUntil, timeZone, "EEEE, MMMM do 'at' h:mm a zzz")}`}
      confirmText={form.formState.isSubmitting ? 'Cancelling...' : 'Yes, Cancel Shutdown'}
      cancelText={'No, Keep Shutdown'}
      triggerText={'Cancel Shutdown'}
    >
      <FormField
        control={form.control}
        name={'cancelShutdownReason'}
        render={({ field }) => (
          <FormItem className={'text-left'}>
            <FormControl>
              <div className={'relative'}>
                <Textarea
                  {...field}
                  data-testid={'cancel-reason'}
                  placeholder={'Briefly explain reasons for cancelling shutdown...'}
                  className={'resize-none text-foreground w-full'}
                  maxLength={500}
                />
                <div className={'absolute bottom-2 right-2 text-sm text-muted-foreground'}>
                  {field.value?.length ?? 0}
                  {'/500'}
                </div>
              </div>
            </FormControl>
            <FormMessage className={'pl-2'} />
          </FormItem>
        )}
      />
    </ShutdownDialog>
  );
};
